/**
 * Filter section for the ASNTab.
 *
 * Extracted to reduce ASNTab line count.
 */

import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { X, Calendar, Search, Filter } from 'lucide-react';
import SearchableSelect from '../../ui/SearchableSelect';

interface FilterOption {
  value: string;
  label: string;
}

interface ASNFilterSectionProps {
  filters: {
    asnNumber: string;
    supplier: string;
    trackingNumber: string;
    poNumber: string;
    poLineNumber: string;
    status: string;
    courier: string;
    courierDeliveryStatus: string;
    dateFrom: string;
    dateTo: string;
    site: string;
    supplierGroup: string;
    commodityGroup: string;
    commodityType: string;
    strategic: string;
  };
  textInputs: {
    asnNumber: string;
    supplier: string;
    trackingNumber: string;
    poNumber: string;
    poLineNumber: string;
  };
  siteOptions: FilterOption[];
  supplierGroupOptions: FilterOption[];
  commodityGroupOptions: FilterOption[];
  commodityTypeOptions: FilterOption[];
  onFilterChange: (key: string, value: string) => void;
  onClearFilters: () => void;
  onSearch: () => void;
  onHideFilters?: () => void;
}

export function ASNFilterSection({
  filters,
  textInputs,
  siteOptions,
  supplierGroupOptions,
  commodityGroupOptions,
  commodityTypeOptions,
  onFilterChange,
  onClearFilters,
  onSearch,
  onHideFilters,
}: ASNFilterSectionProps) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Filter ASNs</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {/* Row 1: Site, Supplier Group, Supplier, Strategic */}
          <div>
            <label className="text-sm font-medium mb-2 block">Company Site</label>
            <SearchableSelect
              options={siteOptions}
              value={filters.site}
              onChange={(val) => onFilterChange('site', val)}
              placeholder="All Company Sites"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Supplier Group</label>
            <SearchableSelect
              options={supplierGroupOptions}
              value={filters.supplierGroup}
              onChange={(val) => onFilterChange('supplierGroup', val)}
              placeholder="All Supplier Groups"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Supplier Site</label>
            <input
              type="text"
              placeholder="Supplier site name..."
              value={textInputs.supplier}
              onChange={(e) => onFilterChange('supplier', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            />
          </div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.strategic === 'yes'}
                onChange={(e) => onFilterChange('strategic', e.target.checked ? 'yes' : '')}
                className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
              />
              <span className="text-sm font-medium">Strategic Only</span>
            </label>
          </div>

          {/* Row 2: Commodity Group, Commodity Type, Date From, Date To */}
          <div>
            <label className="text-sm font-medium mb-2 block">Commodity Group</label>
            <select
              value={filters.commodityGroup}
              onChange={(e) => onFilterChange('commodityGroup', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            >
              <option value="">All</option>
              {commodityGroupOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Commodity Type</label>
            <select
              value={filters.commodityType}
              onChange={(e) => onFilterChange('commodityType', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            >
              <option value="">All</option>
              {commodityTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Date From</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => onFilterChange('dateFrom', e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Date To</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => onFilterChange('dateTo', e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
              />
            </div>
          </div>

          {/* Row 3: PO Number, ASN Status, Courier, Tracking Number */}
          <div>
            <label className="text-sm font-medium mb-2 block">PO Number</label>
            <input
              type="text"
              placeholder="PO Number..."
              value={textInputs.poNumber}
              onChange={(e) => onFilterChange('poNumber', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">ASN Status</label>
            <select
              value={filters.status}
              onChange={(e) => onFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            >
              <option value="">All</option>
              <option value="created">Created</option>
              <option value="submitted">Submitted</option>
              <option value="in_transit">In Transit</option>
              <option value="delivered">Delivered</option>
              <option value="received">Received</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Courier</label>
            <select
              value={filters.courier}
              onChange={(e) => onFilterChange('courier', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            >
              <option value="">All</option>
              <option value="fedex">FedEx</option>
              <option value="ups">UPS</option>
              <option value="dhl">DHL</option>
              <option value="tnt">TNT</option>
              <option value="dpd">DPD</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Tracking Number</label>
            <input
              type="text"
              placeholder="Tracking number..."
              value={textInputs.trackingNumber}
              onChange={(e) => onFilterChange('trackingNumber', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            />
          </div>

          {/* Row 4: PO Line Number, ASN Number, Courier Delivery Status */}
          <div>
            <label className="text-sm font-medium mb-2 block">PO Line Number</label>
            <input
              type="text"
              placeholder="Line number..."
              value={textInputs.poLineNumber}
              onChange={(e) => onFilterChange('poLineNumber', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">ASN Number</label>
            <input
              type="text"
              placeholder="ASN Number..."
              value={textInputs.asnNumber}
              onChange={(e) => onFilterChange('asnNumber', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Courier Delivery Status</label>
            <select
              value={filters.courierDeliveryStatus}
              onChange={(e) => onFilterChange('courierDeliveryStatus', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="in_transit">In Transit</option>
              <option value="out_for_delivery">Out for Delivery</option>
              <option value="delivered">Delivered</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        {/* Search + Clear + Hide buttons */}
        <div className="flex gap-3 mt-4 pt-4 border-t">
          <Button onClick={onSearch} className="px-6">
            <Search className="h-4 w-4 mr-2" />
            Search
          </Button>
          <Button variant="outline" onClick={onClearFilters} className="px-6">
            <X className="h-4 w-4 mr-2" />
            Clear All
          </Button>
          {onHideFilters && (
            <Button variant="outline" size="sm" onClick={onHideFilters}>
              <Filter className="h-4 w-4 mr-2" />
              Hide Filters
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
